import DateTimeDisplay from '@/components/DateTime';
import { currencyFormatter } from '@/helpers/currencyFormatter';
import { StandardHoldbackSelfServiceClientData } from '@/types/Holdbacks';
import { Button, Column, Table } from '@palmetto/palmetto-components';
import { memo, useMemo } from 'react';

interface ActiveStandardHoldbacksTableProps {
  holdbacks: StandardHoldbackSelfServiceClientData[];
  isLoading: boolean;
  onArchive: (row: StandardHoldbackSelfServiceClientData) => void;
  canArchive?: boolean;
}

export const ActiveStandardHoldbacksTable = memo(
  ({ holdbacks, isLoading, onArchive, canArchive = true }: ActiveStandardHoldbacksTableProps) => {
    const columns = useMemo(
      () => [
        {
          heading: 'State',
          dataKey: 'state',
          render: (cell: string) => cell,
        },
        {
          heading: 'Organization Name',
          dataKey: 'organizationName',
          render: (cell: string) => cell,
        },
        {
          heading: 'Amount',
          dataKey: 'amount',
          render: (cell: number) => currencyFormatter.format(cell),
        },
        {
          heading: 'Created',
          render: (_cell: unknown, row: StandardHoldbackSelfServiceClientData) => {
            const dateValue = row.createdBy?.date ?? row.createdDate;
            return dateValue ? (
              <DateTimeDisplay value={dateValue.toString()} showDuration={false} dateFormat="DD h:mm:ss a" inline />
            ) : (
              '---'
            );
          },
        },
        {
          heading: 'Created By',
          dataKey: 'createdBy',
          // Note: Optional chaining used because createdBy field may be undefined in "next" environment
          render: (cell: StandardHoldbackSelfServiceClientData['createdBy']) => cell?.user?.name || '---',
        },
        {
          render: (_cell: unknown, row: StandardHoldbackSelfServiceClientData) => (
            <Button
              variant="secondary"
              iconPrefix="trash"
              size="sm"
              tone="neutral"
              onClick={() => onArchive(row)}
              isDisabled={!canArchive}
            />
          ),
        },
      ],
      [onArchive, canArchive],
    );
    return (
      <Table
        rowKey="id"
        columns={columns as Column[]}
        rows={holdbacks}
        align="left"
        isScrollable={{
          x: false,
          y: true,
        }}
        isLoading={isLoading}
      />
    );
  },
);

ActiveStandardHoldbacksTable.displayName = 'ActiveStandardHoldbacksTable';
